'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import React, { useContext, useEffect, useState, useRef } from 'react'
import {
  FaArrowUp,
  FaChevronUp,
  FaEyeSlash,
  FaFire,
  FaImage,
  FaLeaf,
  FaLock,
  FaLongArrowAltRight,
  FaMagic,
  FaPhotoVideo,
  FaUsers,
  FaVideo,
} from 'react-icons/fa'
import { FiStar } from 'react-icons/fi'
import { BsChatDotsFill } from 'react-icons/bs'
import NavHint from '../../../component/NavHint'

import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import Card from '../../../component/Card'
import { useAuth } from '../../../contexts/AuthContext'
import { ImageContext } from '../../../contexts/ImageContext'

import Link from 'next/link'
import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import Cookies from 'js-cookie'
import { ClipLoader } from 'react-spinners'
import Modal from '../../../component/Modal'
import ImmersiveFeed from '../../../component/ImmersiveFeed'
import FilterDropdown from '../../../component/FilterDropdown'
import useFeedScrollMemory from '../../../helpers/feedScrollMemory'
import {
  columnsCountBreakPoints,
  restrictedWords,
  gutter,
} from '../../../helpers/config'
import { safeGetItem, safeRemoveItem } from '../../../helpers/safeStorage'
import { videoSettingsFromDoc } from '../../../helpers/videoVersions'
import {
  activeButtonStyle,
  containsRestrictedWords,
  growthFunction,
  inactiveButtonStyle,
  isSafeCountry,
  toSliderNumber,
} from '../../../helpers/helpers'
import { staticImagesSuperSafe } from '../../../config/staticImages'
import { useTranslation } from 'react-i18next'
import { ExploreContext } from '../../../contexts/ExploreContext'
import { exploreImages, exploreImagesSafe } from '../../../config/exploreImages'
import * as gemsApi from '../../../lib/api/gems.ts'

// Capitalize the first letter for the time-range filter labels. The shared `month`
// translation is lowercase on purpose (it doubles as the "/month" pricing suffix on
// the upgrade page), so we can't fix it at the key; capitalize it here to match the
// already-capitalized Day/Week. Idempotent for those, and a no-op for CJK scripts.
const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// Feed tabs, collapsed into a single dropdown (was 4 pill buttons). Each keeps its
// icon; its guard behaviour is encoded as flags consumed by `selectTab`:
// `blockSafe` shows the safe-mode warning instead of switching, `needUser` routes
// logged-out users to sign up.
const FEED_TABS = [
  // Top is safe-mode accessible (the backend returns SFW results for it); Trending
  // and Newest are not — `blockSafe` pops the safe-mode warning and keeps the user
  // on their current (accessible) tab instead of switching.
  { key: 'Top', labelKey: 'top', Icon: FaArrowUp },
  {
    key: 'Trending',
    labelKey: 'trending',
    Icon: FaFire,
    needUser: true,
    blockSafe: true,
  },
  {
    key: 'Feed',
    labelKey: 'newest',
    Icon: FaLeaf,
    blockSafe: true,
    needUser: true,
  },
  { key: 'Following', labelKey: 'following', Icon: FaUsers, needUser: true },
]

// Explore defaults to the Top feed, but `activeButton` is SHARED context state that
// the SEO landing pages also read (their fresh-visitor content keys off its
// 'Trending' default), so we can't change the global default without shifting those
// pages. Instead we flip to 'Top' only on the first Explore mount of the session —
// and only if the tab is still the pristine 'Trending' default, so a user's own tab
// choice (persisted across in-app navigation) is never overridden. Module-scoped so
// it survives remounts and resets on a hard reload (when the context resets too).
let exploreDefaultedToTop = false

const Explore = () => {
  const { t } = useTranslation()

  const searchParams = useSearchParams()
  const searchQuery = searchParams.get('searchQuery')
  const { user, safe, isPlusOrHigher, authResolved } = useAuth()
  const safeCountry = isSafeCountry()

  const router = useRouter()
  const {
    activeButton,
    setActiveButton,
    model,
    setModel,
    totalImageCount,
    setTotalImageCount,
    images,
    setImages,
    filteredImages,
    setFilteredImages,
  } = useContext(ImageContext)
  const {
    topImagesByMediaType,
    setTopImagesByMediaType,
    trendingImagesByMediaType,
    setTrendingImagesByMediaType,
    featuredImagesByMediaType,
    setFeaturedImagesByMediaType,
  } = useContext(ExploreContext)

  const [loadingImages, setLoadingImages] = useState(false)
  const [welcomeModal, setWelcomeModal] = useState(false)
  const [upgradeModal, setUpgradeModal] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [safeWarningOpen, setSafeWarningOpen] = useState(false)
  const [hasScrolledPassed, setHasScrolledPassed] = useState(false)
  const [mediaType, setMediaType] = useState('all')
  const [orientation, setOrientation] = useState('all')
  // Trending time range. 'month' is the default (a day's window is too sparse to feel
  // like a trending feed); the dropdown's "modified" highlight below tracks this.
  const [dateRange, setDateRange] = useState('month')

  // Capture this during render because the defaulting effect updates the
  // module-scoped flag before the feed-loading effect runs. This prevents the
  // pristine shared Trending default from issuing a request during the brief
  // first-render transition to Top, without gating a real Trending visit.
  const isDefaultingToTop =
    !exploreDefaultedToTop && activeButton === 'Trending'

  const imageFetchCount = useRef(0)
  const staticImagesRef = useRef(null)

  // ───── Use original arrays (no shuffle) ─────
  const staticImages = safeCountry
    ? staticImagesSuperSafe
    : !user
      ? exploreImagesSafe
      : safe === null
        ? []
        : safe
          ? exploreImagesSafe
          : exploreImages

  const isFilteredView =
    model !== 'all' || mediaType !== 'all' || orientation !== 'all'

  // The free Featured block — and the unlock sentinel underneath it — only render
  // on an unfiltered Top view. (`staticImages` is empty until `safe` resolves, so
  // this also covers the pre-auth window.)
  const showFeaturedBlock =
    activeButton === 'Top' && !isFilteredView && staticImages?.length > 0

  // Cost gate for the paid Firebase feed: hold Top until the user scrolls past
  // Featured. It must key off the unfiltered Top *view*, not off Featured actually
  // being rendered — otherwise the brief pre-auth window where `staticImages` is
  // empty would leak an ungated fetch. Filtered views are never gated: applying a
  // filter hides Featured and takes the sentinel with it, so a gate there would
  // strand the user on an empty feed with no way to unlock it.
  const topFeedGated =
    activeButton === 'Top' && !isFilteredView && !hasScrolledPassed

  useEffect(() => {
    // safeGetItem: a bare localStorage read throws on blocked-storage iOS Safari and
    // would crash this page into the error boundary; the cookie fallback still works.
    const welcomeBack =
      safeGetItem('welcomeBack100v8') || Cookies.get('welcomeBack100v8')
    if (welcomeBack === 'true') {
      setWelcomeModal(true)
    }
  }, [])

  // Default Explore to the Top feed on the first visit of the session (see the
  // note by `exploreDefaultedToTop`). The transient 'Trending' render before this
  // runs is ignored by `isDefaultingToTop` below, so no Firebase read fires
  // during the flip.
  useEffect(() => {
    if (exploreDefaultedToTop) return
    exploreDefaultedToTop = true
    if (activeButton === 'Trending') setActiveButton('Top')
  }, [activeButton, setActiveButton])

  useEffect(() => {
    if (model !== 'all') {
      setMediaType('image')
    } else {
      setMediaType('all')
    }
  }, [model])

  const isPaidUser = isPlusOrHigher

  // Switch feed tab from the dropdown, preserving each tab's original guards.
  const selectTab = (tab) => {
    if (!tab) return
    if (tab.blockSafe && safe) {
      setSafeWarningOpen(true)
      return
    }
    if (fetching) return
    if (tab.needUser && !user) {
      router.push('/signup')
      return
    }
    setActiveButton(tab.key)
  }

  // Fullscreen immersive feed on all viewports: a left-click on a tile opens it
  // in place of navigating to /image/{id} (middle/ctrl-click still opens the
  // detail page in a new tab via the underlying link).
  // `source` picks which list the viewer swipes through: the paginated feed or
  // the (non-paginated) Featured seed block.
  const [viewer, setViewer] = useState({
    open: false,
    index: 0,
    source: 'feed',
  })

  // Keep-your-spot: remember the top-most tile on scroll, restore it on return,
  // and center the reel you were on when the viewer closes.
  const { focusTile, saveAnchor } = useFeedScrollMemory('explore')
  const [pendingFocusId, setPendingFocusId] = useState(null)
  // rAF so the anchor scroll runs on the next frame — after the viewer's
  // post-paint scroll-lock restore, which would otherwise win and drop us back
  // where the viewer was opened.
  useEffect(() => {
    if (!pendingFocusId) return
    const raf = requestAnimationFrame(() => {
      focusTile(pendingFocusId)
      setPendingFocusId(null)
    })
    return () => cancelAnimationFrame(raf)
  }, [pendingFocusId, focusTile])

  // Back-to-top button (shown once the grid is scrolled down a bit).
  const [showBackToTop, setShowBackToTop] = useState(false)
  useEffect(() => {
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        setShowBackToTop(window.scrollY > 700)
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const [searchModel, setSearchModel] = useState('All')

  const fetchFromApi = async () => {
    if (!user) {
      return
    }
    try {
      const idToken = await user.getIdToken()

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/search_by_uids`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
        },
      )

      if (!response.ok) {
        throw new Error('API call failed')
      }
      imageFetchCount.current += 1

      const data = await response.json()

      // Transform fetched data to the same structure as the other fetch methods
      return data.map((data) => ({
        id: data.id,
        title: data.prompt,
        fetchKey: imageFetchCount.current,
        imageUrl: data.image_url.replace(
          process.env.NEXT_PUBLIC_URL_1,
          process.env.NEXT_PUBLIC_URL_2,
        ),
        description: data.prompt,
        likes: growthFunction(data.likes) || 0,
        nsfw: data.nsfw,
        date: data.date_uploaded,
        uid: data.uid,
        model: data.model,
        width: data.width,
        height: data.height,
        creativity: data.cfg_scale,
        negativePrompt: data.negative_prompt,
        loras: data.loras,
        lorasList: data.loras_list ? data.loras_list : [],

        views: growthFunction(data.views) || 0,
        upscaled: data.upscaled,
        seed: data.seed,
        reports: data.reports || 0,
        filter: data.style_filter,
        prompt_ja: data?.prompt_ja,
        prompt_de: data?.prompt_de,
        prompt_zh: data?.prompt_zh,
        prompt_es: data?.prompt_es,
        negative_prompt_ja: data?.negative_prompt_ja,
        negative_prompt_de: data?.negative_prompt_de,
        negative_prompt_zh: data?.negative_prompt_zh,
        negative_prompt_es: data?.negative_prompt_es,
        details: data?.details,
        expression: data?.expression,
        aspect: data?.aspect ?? null,
        video: data?.video || false,

        videoThumbnail: data?.videoThumbnail,
        videoVersion: data?.version || 1,
        // v4-voice settings, so Clone reproduces the clip exactly. Spread from one shared
        // shape: this list previously omitted the accent and the resolution, so a cloned clip
        // came back with a Default accent at Fast regardless of how it was rendered.
        ...videoSettingsFromDoc(data),
        age_slider: toSliderNumber(data?.age_slider),
        weight_slider: toSliderNumber(data?.weight_slider),
        breast_slider: toSliderNumber(data?.breast_slider),
        ass_slider: toSliderNumber(data?.ass_slider),
        penis_slider: toSliderNumber(data?.penis_slider),
        loop: data?.loop || false,
        frame_rate: data?.frame_rate || 25,
        quality: data?.quality || 'Standard',
        original_image_url: data?.original_image_url ?? null,
        audio_enabled: data?.audio_enabled || null,
      }))
    } catch (error) {
      console.error('Error fetching from API: ', error)
      return []
    } finally {
      setLoadingImages(false)
    }
  }

  useEffect(() => {
    const fetchSearch = async () => {
      if (!user) {
        // Redirect only once auth has actually resolved (the bootstrap timeout can release
        // the loader with user still pending); either way bail out — the code below
        // dereferences `user` and would throw on null.
        if (authResolved) router.push('/signup')
        return
      }

      setImages([])

      const idToken = await user.getIdToken()
      const userId = user && user.uid ? user.uid : null

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/search`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            query: searchQuery,
            user: userId,
            model: searchModel,
          }),
        },
      )

      const searchResults = await response.json()
      imageFetchCount.current += 1

      const imageList = searchResults.map((data) => ({
        id: data.id,
        title: data.prompt,
        fetchKey: imageFetchCount.current,
        imageUrl: data.image_url.replace(
          process.env.NEXT_PUBLIC_URL_1,
          process.env.NEXT_PUBLIC_URL_2,
        ),
        description: data.prompt,
        likes: growthFunction(data.likes) || 0,
        nsfw: data.nsfw,
        date: data.date_uploaded,
        uid: data.uid,
        model: data.model,
        width: data.width,
        height: data.height,
        creativity: data.cfg_scale,
        negativePrompt: data.negative_prompt,
        loras: data.loras,
        lorasList: data.loras_list ? data.loras_list : [],

        views: growthFunction(data.views) || 0,
        upscaled: data.upscaled,
        seed: data.seed,
        reports: data.reports || 0,
        filter: data.style_filter,
        prompt_ja: data?.prompt_ja,
        prompt_de: data?.prompt_de,
        prompt_zh: data?.prompt_zh,
        prompt_es: data?.prompt_es,
        negative_prompt_ja: data?.negative_prompt_ja,
        negative_prompt_de: data?.negative_prompt_de,
        negative_prompt_zh: data?.negative_prompt_zh,
        negative_prompt_es: data?.negative_prompt_es,
        details: data?.details,
        expression: data?.expression,
        aspect: data?.aspect ?? null,
        video: data?.video || false,

        videoThumbnail: data?.videoThumbnail,
        videoVersion: data?.version || 1,
        // v4-voice settings, so Clone reproduces the clip exactly. Spread from one shared
        // shape: this list previously omitted the accent and the resolution, so a cloned clip
        // came back with a Default accent at Fast regardless of how it was rendered.
        ...videoSettingsFromDoc(data),
        age_slider: toSliderNumber(data?.age_slider),
        weight_slider: toSliderNumber(data?.weight_slider),
        breast_slider: toSliderNumber(data?.breast_slider),
        ass_slider: toSliderNumber(data?.ass_slider),
        penis_slider: toSliderNumber(data?.penis_slider),
        loop: data?.loop || false,
        frame_rate: data?.frame_rate || 25,
        quality: data?.quality || 'Standard',
        original_image_url: data?.original_image_url ?? null,
        audio_enabled: data?.audio_enabled || null,
      }))

      setImages(imageList)
    }

    if (searchQuery && searchQuery.trim() !== '') {
      fetchSearch()
      setActiveButton('Search')
    }
  }, [searchQuery, setActiveButton, user, authResolved, setImages, searchModel])

  useEffect(() => {
    if (totalImageCount > 0 || !user) {
      return
    }

    const fetchTotalImages = async () => {
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/total_images`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${await user.getIdToken()}`,
              'Content-Type': 'application/json',
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch total images')
        }

        const data = await response.json()
        setTotalImageCount(data.total_images)
      } catch (error) {
        console.error('Error fetching total images:', error)
      }
    }

    fetchTotalImages()
  }, [totalImageCount, user])

  // ───── Updated IntersectionObserver: wait until the Featured block is rendered ─────
  useEffect(() => {
    if (!showFeaturedBlock) {
      return
    }

    // Unlock the (paid Firebase) feed the moment the user reaches the BOTTOM of
    // the free Featured block — i.e. the sentinel sitting just under it scrolls
    // INTO view. Triggering on enter (not on the sentinel later exiting the top)
    // matters because the feed is still empty while gated: the only thing under
    // the sentinel is the "No images found" line + a long footer, so the
    // sentinel would only clear the top edge a full viewport-height later — deep
    // in the footer, or never at all if the footer is shorter than the viewport,
    // leaving the user stuck on "No images found".
    const unlock = () => setHasScrolledPassed(true)

    // Passive-landing guard: if the sentinel is already on-screen on the
    // observer's initial callback (a short Featured block that fully fits the
    // viewport), that's not scroll intent — hold the feed and let the first real
    // scroll unlock it instead. The Featured grid is dozens of images tall, so
    // in practice the sentinel starts well below the fold and this path is rare.
    let isFirstCallback = true
    const onScrollIntent = () => unlock()
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (isFirstCallback) {
            window.addEventListener('scroll', onScrollIntent, {
              passive: true,
              once: true,
            })
          } else {
            unlock()
          }
        }
        isFirstCallback = false
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      },
    )

    if (staticImagesRef.current) {
      observer.observe(staticImagesRef.current)
    }

    return () => {
      window.removeEventListener('scroll', onScrollIntent)
      if (staticImagesRef.current) {
        observer.unobserve(staticImagesRef.current)
      }
    }
    // `showFeaturedBlock` is the dep because the Featured block (and this sentinel)
    // mount and unmount with it — the session's default-to-Top flip, tab switches,
    // and filter changes all move it. Without it the observer would attach on the
    // first (Featured-less) render and never re-attach to the live sentinel node,
    // so scrolling past Featured would never unlock the feed.
  }, [showFeaturedBlock])
  // ───── Updated: Only run Top‐cache logic after staticImages exist and sentinel is set ─────
  useEffect(() => {
    // If we have a live searchQuery, skip all Explore‐cache logic
    if (searchQuery && searchQuery.trim() !== '') {
      return
    }
    // Don’t run until safe/non-null and user is defined
    // if (safe === null || !user) return

    // Decide “sortBy” string from activeButton
    let sortBy
    if (activeButton === 'Top') sortBy = 'top'
    else if (activeButton === 'Trending') sortBy = 'trending'
    else if (activeButton === 'Video') sortBy = 'newest'
    else sortBy = 'newest' // treat anything else as “featured/newest”

    // See if our nested cache already has results for (sortBy, orientation, mediaType,
    // model) — and, for the windowed feeds, the selected date range.
    let imagesToSet = []
    if (sortBy === 'top') {
      imagesToSet =
        topImagesByMediaType?.[orientation]?.[mediaType]?.[model]?.length > 0
          ? topImagesByMediaType[orientation][mediaType][model]
          : []
    } else if (sortBy === 'trending') {
      const bucket =
        trendingImagesByMediaType?.[orientation]?.[mediaType]?.[model]?.[
          dateRange
        ] ?? []
      imagesToSet = bucket.length > 0 ? bucket : []
    } else {
      // “featured” (newest)
      imagesToSet =
        featuredImagesByMediaType?.[orientation]?.[mediaType]?.[model]?.length >
        0
          ? featuredImagesByMediaType[orientation][mediaType][model]
          : []
    }

    if (imagesToSet.length > 0) {
      setImages(imagesToSet)
    } else {
      // Cost gate only Top, which is the tab that renders the free Featured
      // block and its unlock sentinel. Trending must load immediately because
      // it has no Featured block to scroll past. The separate first-render
      // guard avoids a request during Explore's initial Trending-to-Top flip.
      if (isDefaultingToTop) {
        return
      }
      if (topFeedGated) {
        return
      }
      // Safe mode: Top is allowed (backend returns SFW), but Trending is not.
      if (safe && sortBy === 'trending') {
        return
      }
      setImages([]) // clear out any old images
      loadMoreImages() // then fetch from server
    }
    // Added `orientation` to the deps so switching it re-evaluates the cache
  }, [
    model,
    activeButton,
    mediaType,
    orientation,
    user,
    dateRange,
    hasScrolledPassed,
    isDefaultingToTop,
    topFeedGated,
  ])

  useEffect(() => {
    const filtered = images.filter((img) => {
      const isRestricted = containsRestrictedWords(img.title, restrictedWords)
      const isTopOrLowReports =
        activeButton === 'Top' ||
        img.reports < 3 ||
        img.uid === '57qlmsb4JlTEpZmuooqlTgj1rLy2'
      const isNotDisturbing = ![
        'POV Strangling',
        'Tied Up',
        'Tape',
        'Ballgag',
        'Suspension',
        'POV Choking',
      ].includes(img.loras)
      const isNotFur =
        img.model !== 'Fur' ||
        model === 'Fur' ||
        img.model !== 'Furtoon' ||
        model === 'Furtoon'
      const isNotReal = true

      // Allow all videos through:
      if (img.video) return true

      return (
        !isRestricted &&
        isTopOrLowReports &&
        isNotFur &&
        isNotReal &&
        isNotDisturbing
      )
    })

    if (searchQuery) {
      setFilteredImages(images)
    } else {
      setFilteredImages(filtered)
    }
  }, [images, activeButton, searchQuery, model])

  const loadMoreImages = async () => {
    // Only Top renders the free Featured block and its unlock sentinel, and only
    // while unfiltered. Trending — and any filtered Top view — has no sentinel, so
    // it must be allowed to load immediately.
    if (topFeedGated) {
      return
    }

    // Safe mode: Top is allowed (backend returns SFW), but Trending is not.
    if (safe && activeButton === 'Trending') {
      return
    }

    if (!user || fetching) return

    // If we’re in “Following” mode (searchByUids), that logic didn’t change:
    if (activeButton === 'Following') {
      const images = await fetchFromApi()
      setImages(images)
      return
    }

    setLoadingImages(true)
    setFetching(true)

    try {
      const idToken = await user.getIdToken()

      // ─────────────── Build request body ───────────────
      let lastDocId = null

      let sortBy
      if (activeButton === 'Top') sortBy = 'top'
      else if (activeButton === 'Trending') sortBy = 'trending'
      else if (activeButton === 'Video') sortBy = 'newest'
      else sortBy = 'newest' // fallback “featured/newest”

      // Grab whichever nested cache we’re appending to:
      const cacheForThisSort =
        sortBy === 'top'
          ? topImagesByMediaType?.[orientation]?.[mediaType]?.[model] || []
          : sortBy === 'trending'
            ? trendingImagesByMediaType?.[orientation]?.[mediaType]?.[model]?.[
                dateRange
              ] || []
            : featuredImagesByMediaType?.[orientation]?.[mediaType]?.[model] ||
              []

      if (cacheForThisSort.length > 0) {
        lastDocId = cacheForThisSort[cacheForThisSort.length - 1].id
      } else {
        lastDocId = null
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_explore_feed`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            last_doc_id: lastDocId,
            limit: 30,
            safe,
            model,
            media_type: mediaType,
            sort_by: sortBy,
            orientation, // already being sent through
            ...(sortBy === 'trending' ? { date_range: dateRange } : {}),
            // Top is pinned to the last year and deliberately not user-switchable —
            // an unbounded likes leaderboard could never be displaced by new work, so
            // its first screen was permanently a year-plus stale. Sent explicitly
            // rather than relying on the server default so the intent is visible here.
            ...(sortBy === 'top' ? { date_range: 'year' } : {}),
          }),
        },
      )

      if (!response.ok) {
        console.error('Failed to fetch from /get_explore_feed')
        return
      }

      const { images: newImages } = await response.json()
      imageFetchCount.current += 1

      const mappedImages = newImages.map((data) => ({
        id: data.id,
        title: data.prompt,
        fetchKey: imageFetchCount.current,
        imageUrl: data.image_url.replace(
          process.env.NEXT_PUBLIC_URL_1,
          process.env.NEXT_PUBLIC_URL_2,
        ),
        description: data.prompt,
        likes: growthFunction(data.likes) || 0,
        nsfw: data.nsfw,
        date: data.date_uploaded,
        uid: data.uid,
        model: data.model,
        width: data.width,
        height: data.height,
        creativity: data.cfg_scale,
        negativePrompt: data.negative_prompt,
        loras: data.loras,
        lorasList: data.loras_list ? data.loras_list : [],

        views: growthFunction(data.views) || 0,
        upscaled: data.upscaled,
        seed: data.seed,
        reports: data.reports || 0,
        filter: data.style_filter,
        prompt_ja: data?.prompt_ja,
        prompt_de: data?.prompt_de,
        prompt_zh: data?.prompt_zh,
        prompt_es: data?.prompt_es,
        negative_prompt_ja: data?.negative_prompt_ja,
        negative_prompt_de: data?.negative_prompt_de,
        negative_prompt_zh: data?.negative_prompt_zh,
        negative_prompt_es: data?.negative_prompt_es,
        details: data?.details,
        expression: data?.expression,
        aspect: data?.aspect ?? null,
        video: data?.video || false,

        videoThumbnail: data?.videoThumbnail,
        videoVersion: data?.version || 1,
        // v4-voice settings, so Clone reproduces the clip exactly. Spread from one shared
        // shape: this list previously omitted the accent and the resolution, so a cloned clip
        // came back with a Default accent at Fast regardless of how it was rendered.
        ...videoSettingsFromDoc(data),
        age_slider: toSliderNumber(data?.age_slider),
        weight_slider: toSliderNumber(data?.weight_slider),
        breast_slider: toSliderNumber(data?.breast_slider),
        ass_slider: toSliderNumber(data?.ass_slider),
        penis_slider: toSliderNumber(data?.penis_slider),
        loop: data?.loop || false,
        frame_rate: data?.frame_rate || 25,
        quality: data?.quality || 'Standard',
        original_image_url: data?.original_image_url ?? null,
        audio_enabled: data?.audio_enabled || null,
      }))

      const combinedList = mappedImages

      // ───────────────────────────────────────────────────────────────
      // STORE into the correct nested cache (now including orientation)
      // ───────────────────────────────────────────────────────────────
      if (sortBy === 'top') {
        setTopImagesByMediaType((prev) => {
          const next = { ...prev }
          next[orientation] = {
            ...next[orientation],
            [mediaType]: {
              ...next[orientation][mediaType],
              [model]: [
                ...(next[orientation][mediaType][model] || []),
                ...combinedList,
              ],
            },
          }
          return next
        })
      } else if (sortBy === 'trending') {
        setTrendingImagesByMediaType((prev) => {
          const next = { ...prev }
          next[orientation] = {
            ...next[orientation],
            [mediaType]: {
              ...next[orientation][mediaType],
              [model]: {
                ...next[orientation][mediaType][model],
                [dateRange]: [
                  ...(next[orientation][mediaType][model]?.[dateRange] || []),
                  ...combinedList,
                ],
              },
            },
          }
          return next
        })
      } else {
        setFeaturedImagesByMediaType((prev) => {
          const next = { ...prev }
          next[orientation] = {
            ...next[orientation],
            [mediaType]: {
              ...next[orientation][mediaType],
              [model]: [
                ...(next[orientation][mediaType][model] || []),
                ...combinedList,
              ],
            },
          }
          return next
        })
      }

      // 10) Finally: update the “images” that are currently on‐screen
      setImages((prev) => [...prev, ...combinedList])
    } catch (error) {
      console.error('Error in loadMoreImages:', error)
    } finally {
      setLoadingImages(false)
      setFetching(false)
    }
  }

  useBottomScrollListener(loadMoreImages, {
    offset: 2000, // Offset from the bottom of the page. Adjust this value as per your requirement.
    debounce: 1000,
  })

  const claimWelcomeGems = async () => {
    if (!user) {
      return
    }
    try {
      const idToken = await user.getIdToken()

      const response = await gemsApi.claimWelcomeReward(idToken)

      // The offer is spent once it is paid (2xx) or found claimed already (403); after any other answer the flag
      // stays, so the offer comes back on the next visit.
      if (response.ok || response.status === 403) {
        safeRemoveItem('welcomeBack100v8')
        Cookies.remove('welcomeBack100v8')
      }

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Something went wrong')
      }

      if (typeof window !== 'undefined') {
        window.location.reload()
      }
    } catch {
      // console.log(err)
    }
    setWelcomeModal(false)
  }

  // Results count + safe-mode notice. With Featured up, leaving this at the top
  // stacked three things competing for the same glance — filters, this row, then
  // Featured's heading and CTA — so it moves down to head the main feed it
  // actually describes. One helper, so the two placements can't drift.
  const metaRowVisible =
    !searchQuery && !safeCountry && (Boolean(user) || Boolean(safe))

  const metaRow = (className) =>
    metaRowVisible ? (
      <div className={`flex items-center justify-between ${className}`}>
        {user && (
          <p className="text-white font-semibold text-sm">
            {t('results')} {totalImageCount.toLocaleString('en-US')}
          </p>
        )}

        {safe && !safeCountry && (
          <Link
            href="/settings"
            className="mt-2 inline-flex max-w-lg flex-wrap items-center justify-center gap-2 rounded-2xl border border-[#7a74eb]/25 px-3 py-2 text-sm transition md:ml-auto md:mx-0 mx-auto"
          >
            <FaEyeSlash className="text-[#a5b4fc] shrink-0" />

            <span className="text-gray-300">
              <span className="font-medium text-[#c7d2fe]">
                {t('safeModeEnabled')}
              </span>
            </span>

            <span className="inline-flex items-center rounded-full border border-[#7a74eb]/50 px-3 py-1 text-xs font-semibold text-[#c7d2fe] transition hover:bg-[var(--main-bg-color)]">
              {t('update')}
            </span>
          </Link>
        )}
      </div>
    ) : null

  // Reciprocal of Generate's "Need ideas? Explore" hint, plus its Chat sibling.
  // Both only ride along with the Featured heading — a browsing surface doesn't
  // need a standing CTA on every feed state. Create is the primary action
  // (solid); Chat is the secondary (outline).
  const featuredActions = (
    <div className="flex flex-wrap items-center gap-2 md:shrink-0">
      <NavHint
        href="/generate"
        icon={FaMagic}
        label={t('createYourOwn')}
        variant="solid"
        size="compact"
        className="shrink-0"
      />
      <NavHint
        href="/chat"
        icon={BsChatDotsFill}
        label={t('chatWithThem')}
        size="compact"
        className="shrink-0"
      />
    </div>
  )

  return (
    <div className="bg-[var(--background-bg-color)] min-h-screen">
      <Modal
        isOpen={welcomeModal}
        closeModal={() => setWelcomeModal(false)}
        title={t('thanksForYourPatience')}
        content={t('welcomeGemsDescription')}
        confirmation={t('claimGems')}
        onConfirm={claimWelcomeGems}
      />

      <Modal
        isOpen={upgradeModal}
        closeModal={() => setUpgradeModal(false)}
        title={
          <div className="flex items-center">
            <FaLock className="text-white opacity-70 z-20 mr-2" />
            <p>{t('nsfwContent')}</p>
          </div>
        }
        content={t('signUpToViewNsfwContent')}
        confirmation={t('signUp')}
        onConfirm={() => router.push('/signup')}
      />

      <Modal
        isOpen={safeWarningOpen}
        closeModal={() => {
          setSafeWarningOpen(false)
        }}
        title={t('safeModeEnabledTitle')}
        content={
          <div>
            <p className="text-white">
              {t('safeModeFeatureBlockedDescription')}
            </p>
          </div>
        }
        confirmation={t('settings')}
        onConfirm={() => router.push('/settings')}
      />

      <div className="mx-auto">
        <div className="pt-4 pb-12 md:py-12 px-2 md:px-6 lg:px-8 mx-auto">
          <section className="container mx-auto md:px-4 pt-2 pb-8 md:py-8">
            <div className="flex items-center justify-between mb-4 md:mt-0 mt-2">
              {searchQuery ? (
                <div className="flex flex-col">
                  <div className="relative inline-flex md:pl-2 mb-3">
                    <svg
                      className="w-2 h-2 absolute top-0 right-0 m-4 pointer-events-none"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 412 232"
                    >
                      <path
                        d="M206 232l206-232H0z"
                        fill="#fff"
                        fillRule="evenodd"
                      ></path>
                    </svg>
                    <select
                      id="modelSelector"
                      value={searchModel}
                      onChange={(e) => {
                        setSearchModel(e.target.value)
                      }}
                      className={`rounded-full font-semibold w-40 text-center [text-align-last:center] h-10 px-8 hover:border-gray-400 focus:outline-none appearance-none ${
                        searchModel === 'All'
                          ? inactiveButtonStyle
                          : activeButtonStyle
                      }`}
                    >
                      {/* "All" (not "all") — it has to match this select's initial
                          state, which is what the search API reads as "no style
                          filter". The lowercase value made an explicit "All Styles"
                          pick filter on a style no image has, returning nothing. */}
                      <option value="All">{t('allStyles')}</option>
                      <option value="Real">{t('real')}</option>
                      <option value="Hyperreal"> {t('hyperreal')}</option>
                      <option value="Anime">{t('anime')}</option>
                      <option value="Anime XL">{t('anime xl')}</option>
                      <option value="Cinematic XL">{t('cinematic xl')}</option>
                      <option value="Hardcore XL">{t('hardcore xl')}</option>
                      <option value="Hyperanime">{t('hyperanime')}</option>
                      <option value="K-Pop">{t('k-pop')}</option>
                      <option value="Pony Anime XL">
                        {t('pony anime xl')}
                      </option>
                      <option value="Pony Illustration XL">
                        {t('pony illustration xl')}
                      </option>
                      <option value="Pony Render XL">
                        {t('pony render xl')}
                      </option>

                      <option value="Pony Photo XL 2">
                        {t('pony photo xl 2')}
                      </option>
                      <option value="Pony XL 2">{t('pony xl 2')}</option>
                      <option value="Cinematic Flame">
                        {t('cinematic flame')}
                      </option>
                      <option value="Anime Flame">{t('anime flame')}</option>
                    </select>
                  </div>
                  <p className="text-white font-semibold">
                    {t('searchResults')}
                  </p>
                </div>
              ) : !safeCountry ? (
                <div className="flex flex-col w-full">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Feed sort (Top / Trending / Newest / Following) — icons kept */}
                    <FilterDropdown
                      className="shrink-0 basis-[calc(50%-0.25rem)] md:basis-auto md:w-44"
                      ariaLabel="Feed"
                      active
                      value={activeButton}
                      options={FEED_TABS.map((tb) => ({
                        value: tb.key,
                        label: t(tb.labelKey),
                        Icon: tb.Icon,
                      }))}
                      onSelect={(key) =>
                        selectTab(FEED_TABS.find((tb) => tb.key === key))
                      }
                    />

                    {model === 'all' && (
                      <FilterDropdown
                        className="shrink-0 basis-[calc(50%-0.25rem)] md:basis-auto md:w-40"
                        ariaLabel="Media type"
                        active={mediaType !== 'all'}
                        value={mediaType}
                        options={[
                          {
                            value: 'all',
                            label: t('allMedia'),
                            Icon: FaPhotoVideo,
                          },
                          { value: 'image', label: t('images'), Icon: FaImage },
                          { value: 'video', label: t('videos'), Icon: FaVideo },
                        ]}
                        onSelect={(value) => {
                          if (safe) {
                            setSafeWarningOpen(true)
                            return
                          }
                          if (fetching) return
                          setMediaType(value)
                          if (value === 'video') setModel('all')
                        }}
                      />
                    )}

                    <FilterDropdown
                      className="shrink-0 basis-[calc(50%-0.25rem)] md:basis-auto md:w-40"
                      ariaLabel="Orientation"
                      active={orientation !== 'all'}
                      value={orientation}
                      options={[
                        { value: 'all', label: t('straight') },
                        { value: 'gay', label: t('gay') },
                        { value: 'lesbian', label: t('lesbian') },
                      ]}
                      onSelect={(value) => {
                        if (safe) {
                          setSafeWarningOpen(true)
                          return
                        }
                        if (fetching) return
                        setOrientation(value)
                      }}
                    />

                    {mediaType !== 'video' && (
                      <FilterDropdown
                        className="shrink-0 basis-[calc(50%-0.25rem)] md:basis-auto md:w-44"
                        ariaLabel="Style"
                        active={model !== 'all'}
                        value={model}
                        // Newest → oldest (matches the /generate ModelModal order).
                        options={[
                          { value: 'all', label: t('allStyles') },
                          {
                            value: 'Cinematic Flame',
                            label: t('cinematic flame'),
                          },
                          { value: 'Anime Flame', label: t('anime flame') },
                          {
                            value: 'Pony Photo XL 2',
                            label: t('pony photo xl 2'),
                          },
                          { value: 'Pony XL 2', label: t('pony xl 2') },
                          {
                            value: 'Pony Illustration XL',
                            label: t('pony illustration xl'),
                          },
                          {
                            value: 'Pony Render XL',
                            label: t('pony render xl'),
                          },
                          { value: 'Pony Anime XL', label: t('pony anime xl') },
                          { value: 'Cinematic XL', label: t('cinematic xl') },
                          { value: 'Hardcore XL', label: t('hardcore xl') },
                          { value: 'Anime XL', label: t('anime xl') },
                          { value: 'Real', label: t('real') },
                          { value: 'Anime', label: t('anime') },
                          { value: 'Hyperreal', label: t('hyperreal') },
                          { value: 'Hyperanime', label: t('hyperanime') },
                          { value: 'K-Pop', label: t('k-pop') },
                        ]}
                        onSelect={(value) => {
                          if (safe) {
                            setSafeWarningOpen(true)
                            return
                          }
                          if (fetching) return
                          user ? setModel(value) : router.push('/signup')
                        }}
                      />
                    )}
                  </div>

                  {/* Trending time range — always on its own line below the filters.
                      Trending is the only user-windowed feed: Top is pinned to the
                      last year on purpose and exposes no control. */}
                  {activeButton === 'Trending' && (
                    <div className="mt-2">
                      <FilterDropdown
                        className="w-[calc(50%-0.25rem)] md:w-40"
                        ariaLabel="Time range"
                        active={dateRange !== 'month'}
                        value={dateRange}
                        options={[
                          { value: 'day', label: capFirst(t('day')) },
                          { value: 'week', label: capFirst(t('week')) },
                          { value: 'month', label: capFirst(t('month')) },
                        ]}
                        onSelect={(value) => {
                          if (safe) {
                            setSafeWarningOpen(true)
                            return
                          }
                          if (fetching) return
                          setDateRange(value)
                        }}
                      />
                    </div>
                  )}

                  {/* Only here when Featured isn't up — otherwise it renders
                      below Featured, see `metaRow`. */}
                  {!showFeaturedBlock && metaRow('mt-4')}
                </div>
              ) : null}
            </div>

            <>
              {showFeaturedBlock && (
                <>
                  {/* Subtle tinted panel = visual divider from the Top feed
                        below, but no horizontal padding so Featured images stay the
                        same size as the normal feed. */}
                  <div className="mb-2 overflow-hidden rounded-xl bg-[var(--middle-bg-color)] py-3 md:p-4">
                    {/* Actions opposite the heading from md up; below the text
                        on phones. Two pills plus "Featured" need ~430px on one
                        line and a 360px phone has ~320px, so a single row would
                        force one of them to wrap on its own anyway — stacking
                        keeps them a deliberate pair instead.

                        Horizontal padding lives here rather than on the panel:
                        the panel stays flush on mobile so the masonry keeps the
                        normal feed's image size (see above), which is what left
                        this text and button sitting on the edge. */}
                    <div className="mb-4 md:mb-6 px-3 md:px-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
                      <div className="max-w-md">
                        <h2 className="text-2xl md:text-3xl font-bold text-white my-2 flex items-center">
                          <FiStar className="text-[var(--main-bg-color)] mr-2 shrink-0" />
                          {t('featured')}
                        </h2>
                        <p className="mt-2 text-sm text-gray-400">
                          {t('aColletionOfSexy')}
                        </p>
                      </div>
                      {featuredActions}
                    </div>
                    <ResponsiveMasonry
                      columnsCountBreakPoints={columnsCountBreakPoints}
                    >
                      <Masonry gutter={gutter}>
                        {staticImages.map((image, index) => (
                          <div
                            key={`featured-${image.id}-${index}`}
                            className="overflow-hidden rounded-xl"
                          >
                            <Card
                              {...image}
                              hideOverlaysMobile
                              autoplay
                              popImage={(imageId) => {
                                setImages(
                                  images.filter((img) => img.id !== imageId),
                                )
                              }}
                              imageData={image}
                              upgradeModal={upgradeModal}
                              setUpgradeModal={setUpgradeModal}
                              nsfw={safe && image?.nsfw}
                              onOpen={
                                user
                                  ? () =>
                                      // Do NOT unlock the feed here — a casual
                                      // glance at a Featured reel must stay free.
                                      // The feed unlocks lazily in onLoadMore
                                      // (see below) only when swiping to its end.
                                      setViewer({
                                        open: true,
                                        index,
                                        source: 'featured',
                                      })
                                  : undefined
                              }
                            />
                          </div>
                        ))}
                      </Masonry>
                    </ResponsiveMasonry>
                  </div>

                  {/* Sentinel just below the Featured block */}
                  <div
                    ref={staticImagesRef}
                    style={{ height: '1px', width: '100%' }}
                  />

                  <div className="my-16"></div>

                  {/* Moved down from the filter row: past Featured it reads as
                      the heading for the feed it counts, instead of a third
                      thing to parse before the page has shown any images. */}
                  {metaRow('mb-4')}
                </>
              )}

              {user && !safeCountry ? (
                <>
                  {filteredImages && filteredImages.length > 0 ? (
                    <>
                      {!isPaidUser ? (
                        <ResponsiveMasonry
                          columnsCountBreakPoints={columnsCountBreakPoints}
                        >
                          <Masonry gutter={gutter}>
                            {filteredImages.map((image, index) => (
                              <React.Fragment
                                key={`${image.id}-${image.fetchKey}`}
                              >
                                <div className="overflow-hidden rounded-xl">
                                  <Card
                                    {...image}
                                    windowed
                                    previewMode
                                    hideOverlaysMobile
                                    popImage={(imageId) => {
                                      setImages(
                                        images.filter(
                                          (img) => img.id !== imageId,
                                        ),
                                      )
                                    }}
                                    imageData={image}
                                    upgradeModal={upgradeModal}
                                    setUpgradeModal={setUpgradeModal}
                                    nsfw={safe && image?.nsfw}
                                    onOpen={() =>
                                      setViewer({
                                        open: true,
                                        index,
                                        source: 'feed',
                                      })
                                    }
                                  />
                                </div>
                              </React.Fragment>
                            ))}
                          </Masonry>
                        </ResponsiveMasonry>
                      ) : (
                        <ResponsiveMasonry
                          columnsCountBreakPoints={columnsCountBreakPoints}
                        >
                          <Masonry gutter={gutter}>
                            {filteredImages.map((image, index) => (
                              <div
                                key={`${image.id}-${image.fetchKey}`}
                                className="overflow-hidden rounded-xl"
                              >
                                <Card
                                  {...image}
                                  windowed
                                  previewMode
                                  hideOverlaysMobile
                                  popImage={(imageId) => {
                                    setImages(
                                      images.filter(
                                        (img) => img.id !== imageId,
                                      ),
                                    )
                                  }}
                                  imageData={image}
                                  upgradeModal={upgradeModal}
                                  setUpgradeModal={setUpgradeModal}
                                  nsfw={safe && image?.nsfw}
                                  onOpen={() =>
                                    setViewer({
                                      open: true,
                                      index,
                                      source: 'feed',
                                    })
                                  }
                                />
                              </div>
                            ))}
                          </Masonry>
                        </ResponsiveMasonry>
                      )}
                    </>
                  ) : loadingImages || safe === null ? (
                    <p className="text-white text-center my-12">
                      <ClipLoader color="#ffffff" size={24} />
                    </p>
                  ) : (
                    <>
                      {safe && activeButton === 'Trending' ? (
                        <div className="flex justify-center my-12">
                          <button
                            onClick={() => setSafeWarningOpen(true)}
                            className="flex items-center justify-center px-5 w-72 max-w-full py-3 bg-[var(--main-bg-color)] text-white rounded-full font-semibold shadow-lg transition-transform duration-200 hover:scale-[1.05]"
                          >
                            More Images
                            <FaLongArrowAltRight className="ml-2" />
                          </button>
                        </div>
                      ) : activeButton === 'Following' &&
                        filteredImages.length === 0 ? (
                        <p className="text-white text-center my-12">
                          No images from the users you're following. Start
                          following users to see their content here.
                        </p>
                      ) : (
                        <p className="text-white text-center my-12">
                          No images found
                        </p>
                      )}
                    </>
                  )}
                </>
              ) : (
                <Link href="/signup">
                  <div className="flex items-center justify-center px-5 w-72 max-w-full py-3 my-20 bg-[var(--main-bg-color)] text-white rounded-full focus:outline-none ml-auto mr-auto font-semibold shadow-lg md:text-lg text-center transition-transform duration-200 hover:scale-[1.05]">
                    {t('signUpToViewMore')}{' '}
                    <FaLongArrowAltRight className="ml-2" />
                  </div>
                </Link>
              )}
            </>
          </section>
        </div>
      </div>

      {showBackToTop && (
        <button
          type="button"
          aria-label="Back to top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed z-40 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--foreground-bg-color)] text-white shadow-lg transition hover:opacity-90 active:scale-90"
          style={{
            right: 'calc(env(safe-area-inset-right, 0px) + 1rem)',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
          }}
        >
          <FaChevronUp />
        </button>
      )}

      {viewer.open && (
        <ImmersiveFeed
          items={
            // Featured flows straight into the Top feed: swipe past the seed
            // collection and keep going into the paginated feed.
            viewer.source === 'featured'
              ? [...staticImages, ...filteredImages]
              : filteredImages
          }
          startIndex={viewer.index}
          onClose={(activeId) => {
            setViewer({ open: false, index: 0, source: 'feed' })
            if (activeId) {
              saveAnchor(activeId)
              setPendingFocusId(activeId)
            }
          }}
          onLoadMore={() => {
            // Lazily unlock the feed only when the user reaches the end of the
            // Featured set — keeps a casual glance free (the flip triggers the
            // fetch effect; loadMore paginates once unlocked).
            if (viewer.source === 'featured' && !hasScrolledPassed) {
              // Unlock only — the hasScrolledPassed effect fetches the first
              // page. Also calling loadMoreImages() here would double-fetch it.
              setHasScrolledPassed(true)
              return
            }
            loadMoreImages()
          }}
          safe={safe}
          user={user}
        />
      )}
    </div>
  )
}

export default Explore
